import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { R as GeneralResources } from '@/Resources.resx';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import {
    SundVaegtProgramDto,
    UpsertWeightLog,
    WeightLogResponse,
} from '@/shared/interfaces/api/sundvaegt-interfaces';
import { fetchSundVaegtProgramQueryAtom } from './fetchSundVaegtProgramQueryAtom';
import { upsertWeightLog } from '../api/upsertWeightLog';

const queryKey = ['sundvaegt-weightprogram'] as const;

export const upsertWeightLogMutationAtom = atomWithMutation<
    WeightLogResponse, // TData (return type)
    Omit<UpsertWeightLog, 'ProgramId'>, // TVariables (you pass DesiredWeightKg only)
    Error, // TError
    { previous?: SundVaegtProgramDto } // TContext (used for rollback)
>((get) => {
    const queryClient = get(queryClientAtom);

    return {
        mutationFn: async (variables: Omit<UpsertWeightLog, 'ProgramId'>) => {
            // read the freshest program right before calling the API
            const program = get(fetchSundVaegtProgramQueryAtom).data;

            const isEditing = variables?.Id != null;

            if (!program) {
                throw new Error('No SundVægt program loaded');
            }
            try {
                return await upsertWeightLog({
                    ProgramId: program.Id,
                    ...variables,
                });
            } catch (error) {
                throw new Error(
                    `${
                        isEditing
                            ? R.SundvaegtEditWeightLogErrorMessage
                            : R.SundvaegtAddWeightLogErrorMessage
                    }: ${
                        error instanceof Error
                            ? error.message
                            : GeneralResources.UnknownError
                    }`,
                );
            }
        },

        // optimistic update
        onMutate: async (variables: Omit<UpsertWeightLog, 'ProgramId'>) => {
            await queryClient.cancelQueries({ queryKey });

            const previous =
                queryClient.getQueryData<SundVaegtProgramDto>(queryKey);

            const isEditing = variables?.Id != null;

            if (previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(queryKey, {
                    ...previous,
                    WeightLogs: isEditing
                        ? previous.WeightLogs.map((log) =>
                              log.Id === variables.Id
                                  ? {
                                        ...log,
                                        ...variables,
                                        ProgramId: previous.Id,
                                    }
                                  : log,
                          )
                        : [
                              ...previous.WeightLogs,
                              {
                                  ...variables,
                                  Id: variables.Id ?? undefined,
                                  ProgramId: previous.Id,
                              },
                          ],
                });
            }

            return { previous };
        },

        // rollback on error
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(
                    queryKey,
                    ctx.previous,
                );
            }
        },

        // ensure server truth after success
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    };
});
